import {
  Map as ImmutableMap,
  merge,
} from 'immutable';

import Facet from '../../views/filter/facet/Facet';
import Filter from '../../modules/filter/Filter';
import FilterPriceRange from '../../views/filter/price/FilterPriceRange';

/* Utils */
import {
  generateInitialSelectedFiltersByFacetMap,
  clearExistingSelectedFiltersByFacetMap,
  generateDeliveryResponseFacetMapper,
  generateCampaignFilterResponseFacetMapper,
} from '../../modules/filter/filterUtils';
import {
  generateFacetMap,
  generateAppliedFacetArray,
  expandFacetVisibleFilterListBy,
  mapFacetItemsByKeyword,
  mergeFacetItems,
} from '../../views/filter/facet/facetUtils';

/* Constants */
import {
  CAMPAING_FILTER_FACET_ID,
  CUSTOM_PRICE_RANGE_FILTER_ID,
  FILTER_PAGINATION_SIZE,
  NEXT_DAY_DELIVERY_FACET_CUSTOM_ID,
  EXPRESS_DELIVERY_FACET_CUSTOM_ID,
} from '../../modules/filter/filterConstants';
import {
  FACET_IDS,
} from '../../views/filter/facet/facetConstants';
import {ROUTER_REDUX_ACTION_NAME} from '../../core/route/routeConstants';
import {getHurlyUrlWhenSortingIsHardcoded} from '../../common/url/urlUtils';

function generateDefaultInitialFilterState() {
  return {
    activeFacet: null,
    facetMap: null,
    filteredCategory: null,
    filterSearchKeyword: ImmutableMap(),
    categoryFacet: null,
    initialFilteredCategory: null,
    shouldPreserveInitialFilteredCategory: false,
    selectedFiltersByFacetMap: null,
    visibleSelectedFiltersByFacetMap: null,
    totalFilteredProductCount: null,
    shouldDisplayColorPalette: false,
    shouldDisplaySearchInput: ImmutableMap(),
    priceRange: new FilterPriceRange(),
    previouslySelectedPriceFiltersMap: ImmutableMap(),
    isFilterManagementPanelActive: false,
    isCategoryPage: false,
    isSearchPage: false,
    isTagPage: false,
    isBrandPage: false,
    previousFilterState: null,
    searchContextForSearchFilters: null,
    isTolkienBrandFiltersAppliedForSearchFilters: null,
    ignoreTolkien: null,
    deliveryInfo: null,
    deliveryLocation: null,
    campaignFilters: null,
    pureCampaignFiltersData: null,
    campaignFiltersSelectedValues: null,
    nextDayDelivery: null,
    expressDelivery: null,
    nextDayDeliveryCheckboxChecked: false,
    expressDeliveryCheckboxChecked: false,
    appliedSelectedAnyDeliveryValue: false,
    expressDeliveryWarehouseCodeValues: null,
    expressDeliveryWarehouseCodeSelectedValues: null,
    ignoreGender: false,
    ignoreColor: false,
    sortValue: undefined,
    selectedFilterCount: null,
    selectedFilter: {},
    tempSelectedFilter: {},
    urlForRedirection: '',
    sortItems: []
  };
}

function filterReducer(state = generateDefaultInitialFilterState(), action) {
  let newState;

  switch (action.type) {
    case 'GET_FILTERS_FULFILLED': {
      const responseData = action.payload.response.data.data;
      const filteredCategory = responseData.categories;
      const categoryFacet = Facet.fromRawCategory(filteredCategory);
      const appliedFacetArray = generateAppliedFacetArray(responseData.appliedFacets, state.deliveryInfo);
      const selectedFiltersByFacetMap = generateInitialSelectedFiltersByFacetMap(
        responseData.facets,
        appliedFacetArray,
        categoryFacet,
        state.deliveryInfo,
      );

      const isCategoryFilter = state.activeFacet && state.activeFacet.id === FACET_IDS.CATEGORY;
      let newActiveFacet, priceRange, newInitialFilteredCategory;

      if (isCategoryFilter) {
        if (filteredCategory.children && filteredCategory.children.length > 0) {
          newActiveFacet = categoryFacet;
        } else {
          newActiveFacet = null;
        }
      } else if (state.activeFacet && selectedFiltersByFacetMap.has(state.activeFacet.id)) {
        newActiveFacet = state.activeFacet;
      } else {
        newActiveFacet = null;
      }

      if (state.shouldPreserveInitialFilteredCategory) {
        newInitialFilteredCategory = state.initialFilteredCategory;
      } else {
        newInitialFilteredCategory = filteredCategory;
      }

      if (selectedFiltersByFacetMap.get(FACET_IDS.PRICE)) {
        const customPriceRangeFilter = selectedFiltersByFacetMap.get(FACET_IDS.PRICE).get(CUSTOM_PRICE_RANGE_FILTER_ID);

        if (customPriceRangeFilter) {
          priceRange = FilterPriceRange.fromFilter(customPriceRangeFilter);
        } else {
          priceRange = new FilterPriceRange();
        }
      } else {
        priceRange = new FilterPriceRange();
      }

      const sortItems = responseData?.sortItems?.map(item => ({
        ...item,
        id: item?.key === 'NONE' ? '' : item?.id || item?.key
      }))

      const sortUrlForRedirection = state?.sortValue
        ? sortItems?.find(item => item?.key === state?.sortValue)?.urlForRedirection
        || getHurlyUrlWhenSortingIsHardcoded({hurlyUrlForParams: responseData?.urlForRedirection, sortItemKey: state?.sortValue})
        : ''
      const urlForRedirection = sortUrlForRedirection || responseData?.urlForRedirection;

      newState = {
        ...state,
        responseData,
        initialFilteredCategory: newInitialFilteredCategory,
        activeFacet: newActiveFacet,
        totalFilteredProductCount: responseData.totalProductCount,
        facetMap: generateFacetMap(
          responseData.facets,
        ),
        shouldPreserveInitialFilteredCategory: true,
        selectedFiltersByFacetMap,
        visibleSelectedFiltersByFacetMap: selectedFiltersByFacetMap,
        filteredCategory,
        categoryFacet,
        priceRange,
        shouldDisplayColorPalette: filteredCategory.displayColorPalette,
        appliedFacetArray,
        isCategoryPage: true,
        isSearchPage: false,
        isTagPage: false,
        isBrandPage: false,
        selectedFilterCount: appliedFacetArray?.length,
        urlForRedirection,
        sortItems
      };

      break;
    }

    case 'GET_SEARCH_FILTERS_FULFILLED': {
      const responseData = action.payload.response.data.data;
      const filteredCategory = responseData.categories;
      const categoryFacet = Facet.fromRawCategory(filteredCategory);
      const appliedFacetArray = generateAppliedFacetArray(responseData.appliedFacets, state.deliveryInfo) || [];
      const selectedFiltersByFacetMap = generateInitialSelectedFiltersByFacetMap(
        responseData.facets,
        appliedFacetArray,
        categoryFacet,
        state.deliveryInfo,
      );

      let newActiveFacet, priceRange, newInitialFilteredCategory;

      if (state.activeFacet && state.activeFacet.id === FACET_IDS.CATEGORY) {
        if (filteredCategory.children.length > 0) {
          newActiveFacet = categoryFacet;
        } else {
          newActiveFacet = null;
        }
      } else if (state.activeFacet && selectedFiltersByFacetMap.has(state.activeFacet.id)) {
        newActiveFacet = state.activeFacet;
      } else {
        newActiveFacet = null;
      }

      if (state.shouldPreserveInitialFilteredCategory) {
        newInitialFilteredCategory = state.initialFilteredCategory;
      } else {
        newInitialFilteredCategory = filteredCategory;
      }

      if (selectedFiltersByFacetMap.get(FACET_IDS.PRICE)) {
        const customPriceRangeFilter = selectedFiltersByFacetMap.get(FACET_IDS.PRICE).get(CUSTOM_PRICE_RANGE_FILTER_ID);

        if (customPriceRangeFilter) {
          priceRange = FilterPriceRange.fromFilter(customPriceRangeFilter);
        } else {
          priceRange = new FilterPriceRange();
        }
      } else {
        priceRange = new FilterPriceRange();
      }

      newState = {
        ...state,
        responseData,
        initialFilteredCategory: newInitialFilteredCategory,
        activeFacet: newActiveFacet,
        totalFilteredProductCount: responseData.totalProductCount,
        facetMap: generateFacetMap(
          responseData.facets,
        ),
        shouldPreserveInitialFilteredCategory: true,
        selectedFiltersByFacetMap,
        visibleSelectedFiltersByFacetMap: selectedFiltersByFacetMap,
        filteredCategory,
        categoryFacet,
        priceRange,
        shouldDisplayColorPalette: filteredCategory.displayColorPalette,
        appliedFacetArray,
        filterSearchKeyword: ImmutableMap(),
        isCategoryPage: false,
        isSearchPage: true,
        isTagPage: false,
        isBrandPage: false,
        searchContextForSearchFilters: responseData.searchContext,
        isTolkienBrandFiltersAppliedForSearchFilters: responseData.isTolkienBrandFiltersApplied,
        ignoreTolkienForSearchFilters: responseData.ignoreTolkien,
        ignoreGender: responseData.ignoreGender,
        ignoreColor: responseData.ignoreColor,
        selectedFilterCount: appliedFacetArray?.length
      };

      break;
    }

    case 'GET_TAG_FILTERS_FULFILLED': {
      const responseData = action.payload.response.data.data;
      const filteredCategory = responseData.categories;
      const categoryFacet = Facet.fromRawCategory(filteredCategory);
      const appliedFacetArray = generateAppliedFacetArray(responseData.appliedFacets, state.deliveryInfo) || [];
      const selectedFiltersByFacetMap = generateInitialSelectedFiltersByFacetMap(
        responseData.facets,
        appliedFacetArray,
        categoryFacet,
        state.deliveryInfo,
      );
      let newActiveFacet, priceRange, newInitialFilteredCategory;

      if (state.activeFacet && state.activeFacet.id === FACET_IDS.CATEGORY) {
        if (filteredCategory.children.length > 0) {
          newActiveFacet = categoryFacet;
        } else {
          newActiveFacet = null;
        }
      } else if (state.activeFacet && selectedFiltersByFacetMap.has(state.activeFacet.id)) {
        newActiveFacet = state.activeFacet;
      } else {
        newActiveFacet = null;
      }

      if (state.shouldPreserveInitialFilteredCategory) {
        newInitialFilteredCategory = state.initialFilteredCategory;
      } else {
        newInitialFilteredCategory = filteredCategory;
      }

      if (selectedFiltersByFacetMap.get(FACET_IDS.PRICE)) {
        const customPriceRangeFilter = selectedFiltersByFacetMap.get(FACET_IDS.PRICE).get(CUSTOM_PRICE_RANGE_FILTER_ID);

        if (customPriceRangeFilter) {
          priceRange = FilterPriceRange.fromFilter(customPriceRangeFilter);
        } else {
          priceRange = new FilterPriceRange();
        }
      } else {
        priceRange = new FilterPriceRange();
      }

      newState = {
        ...state,
        responseData,
        initialFilteredCategory: newInitialFilteredCategory,
        activeFacet: newActiveFacet,
        totalFilteredProductCount: responseData.totalProductCount,
        facetMap: generateFacetMap(
          responseData.facets,
        ),
        shouldPreserveInitialFilteredCategory: true,
        selectedFiltersByFacetMap,
        visibleSelectedFiltersByFacetMap: selectedFiltersByFacetMap,
        filteredCategory,
        categoryFacet,
        priceRange,
        shouldDisplayColorPalette: filteredCategory.displayColorPalette,
        appliedFacetArray,
        filterSearchKeyword: ImmutableMap(),
        isCategoryPage: false,
        isSearchPage: false,
        isTagPage: true,
        isBrandPage: false,
        selectedFilterCount: appliedFacetArray?.length
      };

      break;
    }

    case 'GET_BRAND_FILTERS_FULFILLED': {
      const responseData = action.payload.response.data.data;
      const filteredCategory = responseData.categories;
      const categoryFacet = Facet.fromRawCategory(filteredCategory);
      const appliedFacetArray = generateAppliedFacetArray(responseData.appliedFacets, state.deliveryInfo) || [];
      const selectedFiltersByFacetMap = generateInitialSelectedFiltersByFacetMap(
        responseData.facets,
        appliedFacetArray,
        categoryFacet,
        state.deliveryInfo,
      );
      let newActiveFacet, priceRange, newInitialFilteredCategory;

      if (state.activeFacet && state.activeFacet.id === FACET_IDS.CATEGORY) {
        if (filteredCategory.children.length > 0) {
          newActiveFacet = categoryFacet;
        } else {
          newActiveFacet = null;
        }
      } else if (state.activeFacet && selectedFiltersByFacetMap.has(state.activeFacet.id)) {
        newActiveFacet = state.activeFacet;
      } else {
        newActiveFacet = null;
      }

      if (state.shouldPreserveInitialFilteredCategory) {
        newInitialFilteredCategory = state.initialFilteredCategory;
      } else {
        newInitialFilteredCategory = filteredCategory;
      }

      if (selectedFiltersByFacetMap.get(FACET_IDS.PRICE)) {
        const customPriceRangeFilter = selectedFiltersByFacetMap.get(FACET_IDS.PRICE).get(CUSTOM_PRICE_RANGE_FILTER_ID);

        if (customPriceRangeFilter) {
          priceRange = FilterPriceRange.fromFilter(customPriceRangeFilter);
        } else {
          priceRange = new FilterPriceRange();
        }
      } else {
        priceRange = new FilterPriceRange();
      }

      newState = {
        ...state,
        responseData,
        initialFilteredCategory: newInitialFilteredCategory,
        activeFacet: newActiveFacet,
        totalFilteredProductCount: responseData.totalProductCount,
        facetMap: generateFacetMap(
          responseData.facets,
        ),
        shouldPreserveInitialFilteredCategory: true,
        selectedFiltersByFacetMap,
        visibleSelectedFiltersByFacetMap: selectedFiltersByFacetMap,
        filteredCategory,
        categoryFacet,
        priceRange,
        shouldDisplayColorPalette: filteredCategory.displayColorPalette,
        appliedFacetArray,
        filterSearchKeyword: ImmutableMap(),
        isCategoryPage: false,
        isSearchPage: false,
        isTagPage: false,
        isBrandPage: true,
        selectedFilterCount: appliedFacetArray?.length
      };

      break;
    }

    case 'GET_NEXT_BRAND_FILTERS_FULFILLED':
    case 'GET_NEXT_TAG_FILTERS_FULFILLED':
    case 'GET_NEXT_SEARCH_FILTERS_FULFILLED':
    case 'GET_NEXT_FILTERS_FULFILLED': {
      const responseData = action.payload.response.data.data;
      const {facetId: updatedFacetId} = action.triggerPayload;
      const originalFacet = state.facetMap.get(updatedFacetId);
      const filterSearchKeyword = state.filterSearchKeyword.get(updatedFacetId);
      const facetItemsArrayByKeyword = mapFacetItemsByKeyword(responseData.items, filterSearchKeyword);
      const updatedFacet = new Facet({
        ...originalFacet.rawFacet,
        hasMoreItem: Boolean(responseData.hasMoreItem),
        items: responseData.items,
        visibleItems: mergeFacetItems(
          filterSearchKeyword ? [] : originalFacet.visibleFilterList.toJS(),
          facetItemsArrayByKeyword,
          FILTER_PAGINATION_SIZE + originalFacet.visibleFilterList.size,
        ),
      });

      newState = {
        ...state,
        activeFacet: updatedFacet,
        facetMap: state.facetMap.set(
          updatedFacetId,
          updatedFacet,
        ),
        shouldDisplaySearchInput: state.shouldDisplaySearchInput.set(updatedFacetId, originalFacet.hasMoreItem),
      };

      break;
    }

    case 'GET_NEXT_VISIBLE_FILTERS': {
      const {
        facetId: updatedFacetId,
      } = action.payload;
      const updatedFacet = expandFacetVisibleFilterListBy(
        state.facetMap.get(updatedFacetId),
        FILTER_PAGINATION_SIZE,
        state.filterSearchKeyword.get(updatedFacetId),
      );

      newState = {
        ...state,
        activeFacet: updatedFacet,
        facetMap: state.facetMap.set(
          updatedFacetId,
          updatedFacet,
        ),
      };

      break;
    }

    case 'GET_FILTERS_BY_KEYWORD': {
      const {
        type,
        keyword,
      } = action.payload;
      const originalFacet = state.facetMap.get(type);
      const facetItemsArrayByKeyword = mapFacetItemsByKeyword(originalFacet.filterList.toJS(), keyword);
      const updatedFacet = new Facet({
        ...originalFacet.rawFacet,
        hasMoreItem: Boolean(originalFacet.hasMoreItem),
        items: originalFacet.filterList.toJS(),
        visibleItems: mergeFacetItems(
          keyword ? [] : originalFacet.visibleFilterList.toJS(),
          facetItemsArrayByKeyword,
          FILTER_PAGINATION_SIZE,
        ),
      });

      newState = {
        ...state,
        activeFacet: updatedFacet,
        facetMap: state.facetMap.set(
          type,
          updatedFacet,
        ),
      };

      break;
    }

    case 'CLEAR_ACTIVE_FACET_FILTERS': {
      let priceRange;

      if (state.activeFacet.id === FACET_IDS.PRICE) {
        priceRange = new FilterPriceRange();
      } else {
        priceRange = state.priceRange;
      }

      newState = {
        ...state,
        priceRange,
        previouslySelectedPriceFiltersMap: ImmutableMap(),
      };

      break;
    }

    case 'CLEAR_ALL_FILTERS': {
      const newSelectedFilterMap = clearExistingSelectedFiltersByFacetMap(
        state.visibleSelectedFiltersByFacetMap,
        Facet.fromRawCategory(state.initialFilteredCategory),
      );

      newState = {
        ...state,
        visibleSelectedFiltersByFacetMap: newSelectedFilterMap,
        selectedFiltersByFacetMap: newSelectedFilterMap,
        priceRange: new FilterPriceRange(),
        previouslySelectedPriceFiltersMap: ImmutableMap(),
        sortValue: ''
      };

      break;
    }

    case 'SET_CLEAR_ALL_FILTERS_CLICKED': {
      newState = {
        ...state,
        clickedClearAllFilters: action.payload.clickedClearAllFilters,
        ignoreGender: true,
        ignoreColor: true,
      };

      break;
    }

    case 'GET_UPDATE_FILTER_SELECTION_FULFILLED': {
      const {
        facetId,
        filter,
        isSingleSelection,
      } = action.triggerPayload;

      const selectedFilterMap = state.visibleSelectedFiltersByFacetMap.get(facetId);
      let newSelectedFilterMap, priceRange, previouslySelectedPriceFiltersMap;

      if (selectedFilterMap.has(filter.id)) {
        newSelectedFilterMap = selectedFilterMap.remove(
          filter.id,
        );
      } else {
        if (isSingleSelection?.isSingleSelection) {
          newSelectedFilterMap = ImmutableMap([[filter.id, filter]]);
        } else {
          newSelectedFilterMap = selectedFilterMap.set(filter.id, filter);
        }
      }

      const visibleSelectedFiltersByFacetMap = state.visibleSelectedFiltersByFacetMap.set(
        facetId,
        newSelectedFilterMap,
      );

      if (facetId === FACET_IDS.PRICE) {
        previouslySelectedPriceFiltersMap = visibleSelectedFiltersByFacetMap.get(
          FACET_IDS.PRICE,
        );
        priceRange = new FilterPriceRange();
      } else {
        previouslySelectedPriceFiltersMap = state.previouslySelectedPriceFiltersMap;
        priceRange = state.priceRange;
      }

      newState = {
        ...state,
        visibleSelectedFiltersByFacetMap,
        priceRange,
        previouslySelectedPriceFiltersMap,
      };

      break;
    }

    case 'SET_FILTER_SEARCH_KEYWORD': {
      const {
        type,
        keyword,
      } = action.payload;

      newState = {
        ...state,
        filterSearchKeyword: state.filterSearchKeyword.set(type, keyword),
      };

      break;
    }

    case 'SET_ALL_FILTERS': {
      const {
        type,
      } = action.payload;

      const originalFacet = state.facetMap.get(type);
      const updatedFacet = new Facet({
        ...originalFacet.rawFacet,
        hasMoreItem: Boolean(originalFacet.hasMoreItem),
        items: originalFacet.filterList.toJS(),
        visibleItems: mergeFacetItems(
          [],
          originalFacet.filterList.toJS(),
          FILTER_PAGINATION_SIZE,
        ),
      });

      newState = {
        ...state,
        activeFacet: updatedFacet,
        facetMap: state.facetMap.set(
          type,
          updatedFacet,
        ),
      };

      break;
    }

    case 'SET_PRICE_FILTER_RANGE': {
      const {
        priceRangeEntry: {
          type,
          value,
        },
      } = action.payload;
      const priceRange = new FilterPriceRange({
        ...state.priceRange,
        [type]: value,
      });
      let visibleSelectedFiltersByFacetMap;

      if (priceRange.minPrice || priceRange.maxPrice) {
        const customPriceRangeFilter = Filter.fromPriceRange(
          priceRange,
        );

        visibleSelectedFiltersByFacetMap = state.visibleSelectedFiltersByFacetMap.set(
          state.activeFacet.id,
          ImmutableMap([[
            customPriceRangeFilter.id,
            customPriceRangeFilter,
          ]]),
        );
      } else {
        visibleSelectedFiltersByFacetMap = state.visibleSelectedFiltersByFacetMap.set(
          FACET_IDS.PRICE,
          state.previouslySelectedPriceFiltersMap,
        );
      }

      newState = {
        ...state,
        visibleSelectedFiltersByFacetMap,
        priceRange,
      };

      break;
    }

    case 'SET_WORD_FILTER': {
      const {
        keyword,
      } = action.payload;

      let visibleSelectedFiltersByFacetMap;

      const customWordFilter = Filter.fromWordFilter(
        keyword,
      );

      visibleSelectedFiltersByFacetMap = state.visibleSelectedFiltersByFacetMap.set(
        state.activeFacet.id,
        ImmutableMap([[
          customWordFilter.id,
          customWordFilter,
        ]]),
      );

      newState = {
        ...state,
        visibleSelectedFiltersByFacetMap,
      };

      break;
    }

    case 'SET_ACTIVE_FACET': {
      let originalFacet;
      let updatedFacet;

      if (action.payload.facet.id !== FACET_IDS.CATEGORY) {
        originalFacet = state.facetMap.get(action.payload.facet.id);
        updatedFacet = new Facet({
          ...originalFacet.rawFacet,
          hasMoreItem: Boolean(originalFacet.hasMoreItem),
          items: originalFacet.filterList.toJS(),
          visibleItems: mergeFacetItems(
            [],
            originalFacet.filterList.toJS(),
            FILTER_PAGINATION_SIZE,
          ),
        });
      }

      newState = {
        ...state,
        filterSearchKeyword: ImmutableMap(),
        activeFacet: action.payload.facet.id === FACET_IDS.CATEGORY ? action.payload.facet : updatedFacet,
      };

      break;
    }

    case 'RESET_FILTER_STATE': {
      newState = generateDefaultInitialFilterState();

      break;
    }

    case 'RESET_ACTIVE_FACET': {
      newState = {
        ...state,
        activeFacet: null,
        visibleSelectedFiltersByFacetMap: state.selectedFiltersByFacetMap,
        filterSearchKeyword: ImmutableMap(),
      };

      break;
    }

    case 'RESET_FILTERS': {
      newState = {
        ...state,
        activeFacet: null,
        priceRange: new FilterPriceRange(),
        filterSearchKeyword: ImmutableMap(),
      };

      break;
    }

    case 'OPEN_FILTER_MANAGEMENT_PANEL': {
      newState = {
        ...state,
        previousFilterState: {
          facetMap: state.facetMap,
          shouldPreserveInitialFilteredCategory: state.shouldPreserveInitialFilteredCategory,
          selectedFiltersByFacetMap: state.selectedFiltersByFacetMap,
          visibleSelectedFiltersByFacetMap: state.visibleSelectedFiltersByFacetMap,
          totalFilteredProductCount: state.totalFilteredProductCount,
          shouldDisplayColorPalette: state.shouldDisplayColorPalette,
          priceRange: state.priceRange,
          previouslySelectedPriceFiltersMap: state.previouslySelectedPriceFiltersMap,
          sortValue: state.sortValue,
        },
        isFilterManagementPanelActive: true
      };

      break;
    }

    case 'CLOSE_FILTER_MANAGEMENT_PANEL': {
      const {
        ignoreTolkienCloseForSearchFilters,
      } = action.payload;

      newState = {
        ...state,
        isFilterManagementPanelActive: false,
        activeFacet: null,
        filterSearchKeyword: ImmutableMap(),
        ignoreTolkienForSearchFilters: ignoreTolkienCloseForSearchFilters,
        clickedClearAllFilters: false,
      };

      break;
    }

    case 'RESTORE_PREVIOUS_FILTER_STATE': {
      newState = {
        ...state,
        ...state.previousFilterState,
        previousFilterState: null,
      };

      break;
    }

    case ROUTER_REDUX_ACTION_NAME: {
      const shouldPreserveInitialFilteredCategory =
        Boolean(action.payload.location.state) &&
        Boolean(action.payload.location.state.shouldPreserveInitialFilteredCategory);

      if (action.isInitialLocationChange || shouldPreserveInitialFilteredCategory) {
        newState = state;
      } else {
        newState = {
          ...state,
          shouldPreserveInitialFilteredCategory: false,
        };
      }
      break;
    }

    case 'GET_DELIVERY_INFO_FULFILLED': {
      const responseData = action.payload.response.data.data;
      const deliveryFacet = generateDeliveryResponseFacetMapper(responseData);
      if (!deliveryFacet) {
        newState = state;
      } else {
        let newFacetMap,
          nextSelectedFiltersMap,
          updatedNextDayDeliveryFacet,
          updatedExpressDeliveryFacet,
          nextSelectedExpressDeliveryFilterMap,
          nextSelectedNextDayDeliveryFilterMap;

        if (state.facetMap) {
          let nextDayDeliveryFacet = deliveryFacet.find(facet => facet.customFacetId === NEXT_DAY_DELIVERY_FACET_CUSTOM_ID)
          let expressDeliveryFacet = deliveryFacet.find(facet => facet.customFacetId === EXPRESS_DELIVERY_FACET_CUSTOM_ID)

          let originalFacetNextDayDelivery = state?.facetMap?.find(obj => obj.customFacetId === NEXT_DAY_DELIVERY_FACET_CUSTOM_ID);
          let originalFacetExpressDelivery = state?.facetMap?.find(obj => obj.customFacetId === EXPRESS_DELIVERY_FACET_CUSTOM_ID);

          if (nextDayDeliveryFacet) {
            updatedNextDayDeliveryFacet = new Facet({
              ...originalFacetNextDayDelivery?.rawFacet,
              id: nextDayDeliveryFacet?.id,
              hasMoreItem: false,
              mordorId: nextDayDeliveryFacet?.id,
              items: nextDayDeliveryFacet?.items,
              customFacetId: NEXT_DAY_DELIVERY_FACET_CUSTOM_ID,
              visibleItems: nextDayDeliveryFacet?.items,
              enabled: nextDayDeliveryFacet.enabled,
            });

            newFacetMap = state.facetMap.remove(
              updatedNextDayDeliveryFacet.id,
            );

            newFacetMap = state.facetMap.set(
              nextDayDeliveryFacet?.id,
              updatedNextDayDeliveryFacet,
            );

            nextSelectedFiltersMap = state.visibleSelectedFiltersByFacetMap.remove(
              updatedNextDayDeliveryFacet.id,
            );

            nextSelectedNextDayDeliveryFilterMap = ImmutableMap([[updatedNextDayDeliveryFacet.filterList.first().id, updatedNextDayDeliveryFacet.filterList.first()]]);

            nextSelectedFiltersMap = nextSelectedFiltersMap.set(
              nextDayDeliveryFacet.id,
              state.nextDayDeliveryCheckboxChecked && nextSelectedNextDayDeliveryFilterMap || new ImmutableMap(),
            );
          }

          if (expressDeliveryFacet) {
            updatedExpressDeliveryFacet = new Facet({
              ...originalFacetExpressDelivery?.rawFacet,
              id: expressDeliveryFacet?.id,
              hasMoreItem: false,
              mordorId: expressDeliveryFacet?.id,
              items: expressDeliveryFacet?.items,
              customFacetId: EXPRESS_DELIVERY_FACET_CUSTOM_ID,
              visibleItems: expressDeliveryFacet?.items,
              enabled: expressDeliveryFacet.enabled,
            });

            newFacetMap = state.facetMap.remove(
              updatedExpressDeliveryFacet?.id,
            );

            newFacetMap = state.facetMap.set(
              expressDeliveryFacet?.id,
              updatedExpressDeliveryFacet,
            );

            nextSelectedFiltersMap = state.visibleSelectedFiltersByFacetMap.remove(
              updatedExpressDeliveryFacet.id,
            );

            nextSelectedExpressDeliveryFilterMap = ImmutableMap([[updatedExpressDeliveryFacet.filterList.first().id, updatedExpressDeliveryFacet.filterList.first()]]);

            nextSelectedFiltersMap = nextSelectedFiltersMap.set(
              expressDeliveryFacet?.id,
              state.expressDeliveryCheckboxChecked && nextSelectedExpressDeliveryFilterMap || new ImmutableMap(),
            );
          }
        }

        newState = {
          ...state,
          deliveryInfo: responseData,
          nextDayDelivery: responseData?.nextDayDelivery,
          expressDelivery: responseData?.expressDelivery,
          nextDayDeliverySelected: Boolean(responseData?.nextDayDelivery?.selectedValues?.length > 0),
          expressDeliverySelected: Boolean(responseData?.expressDelivery?.selectedValues?.length > 0),
          expressDeliveryWarehouseCodeValues: responseData?.expressDelivery?.values?.map((value) => value?.value),
          appliedSelectedAnyDeliveryValue: Boolean(responseData?.nextDayDelivery?.selectedValues?.length > 0 || responseData?.expressDelivery?.selectedValues?.length > 0),
          facetMap: newFacetMap || state.facetMap,
          visibleSelectedFiltersByFacetMap: nextSelectedFiltersMap ? nextSelectedFiltersMap : state.visibleSelectedFiltersByFacetMap,
          selectedFiltersByFacetMap: nextSelectedFiltersMap ? nextSelectedFiltersMap : state.selectedFiltersByFacetMap,
        };
      }

      break;
    }

    case 'GET_QUICK_FILTER_DELIVERY_INFO_FULFILLED': {
      const nextDayDelivery = action?.payload?.response?.nextDayDelivery;
      newState = {
        ...state,
        deliveryInfo: {nextDayDelivery},
      };
      break;
    }

    case 'GET_CAMPAIGN_FILTER_FULFILLED': {
      const responseData = action.payload.response.data.data;
      let updatedFacet, nextSelectedFiltersMap, newFacetMap, campaignFilterFacet;

      if (state.facetMap && responseData) {
        const filterResponseDataFacetId = state.facetMap.find(facet => facet.customFacetId === CAMPAING_FILTER_FACET_ID)
        campaignFilterFacet = generateCampaignFilterResponseFacetMapper(responseData, filterResponseDataFacetId?.id);
        let originalFacet = state.facetMap.map(obj => obj.customFacetId === CAMPAING_FILTER_FACET_ID);

        updatedFacet = new Facet({
          ...originalFacet.rawFacet,
          id: campaignFilterFacet.id,
          hasMoreItem: false,
          mordorId: campaignFilterFacet.mordorId,
          items: campaignFilterFacet.items,
          visibleItems: campaignFilterFacet.items,
          enabled: campaignFilterFacet.enabled,
          title: campaignFilterFacet.title,
          isIndexable: false,
          isIndexableWithBrandFilter: false,
          customFacetId: campaignFilterFacet.mordorId,
        });

        newFacetMap = state.facetMap.remove(
          originalFacet.id,
        );

        newFacetMap = newFacetMap.set(
          campaignFilterFacet.id,
          updatedFacet,
        );
      }

      newState = {
        ...state,
        pureCampaignFiltersData: responseData,
        campaignFilters: campaignFilterFacet,
        facetMap: updatedFacet ? newFacetMap : state.facetMap,
        visibleSelectedFiltersByFacetMap: nextSelectedFiltersMap ? nextSelectedFiltersMap : state.visibleSelectedFiltersByFacetMap,
      };

      break;
    }

    case'SET_DELIVERY_SETTINGS' : {
      const {
        settings,
      } = action.payload;

      newState = {
        ...state,
        expressDeliveryCheckboxChecked: settings.expressDeliverySelected,
        nextDayDeliveryCheckboxChecked: settings.nextDayDeliverySelected,
      };

      break;
    }

    case 'SET_DELIVERY_LOCATION': {
      newState = {
        ...state,
        deliveryLocation: action.payload.location,
      };

      break;
    }

    case 'SET_SORTING' : {
      const {
        sortItem,
      } = action.payload;
      const hurlyUrlForParams = JSON.parse(sessionStorage.getItem('moria_moriaHurlyUrlForParams'));

      newState = {
        ...state,
        sortValue: sortItem?.id,
        urlForRedirection: sortItem?.urlForRedirection || (hurlyUrlForParams ? getHurlyUrlWhenSortingIsHardcoded({hurlyUrlForParams, sortItemKey: sortItem?.id}) : '')
      }

      break;
    }

    case 'SET_SELECTED_FILTER_COUNT' : {
      const {
        selectedFilterCount,
      } = action.payload;

      newState = {
        ...state,
        selectedFilterCount,
      }

      break;
    }

    case 'SET_SELECTED_FILTER': {
      const {selectedFilter} = action.payload;
      const facetId = selectedFilter?.facet?.id;
      let filterId = selectedFilter?.filter?.id;

      if (facetId === FACET_IDS.PRICE) {
        filterId = FACET_IDS.PRICE
      }

      newState = {
        ...state,
        tempSelectedFilter: {
          ...state?.tempSelectedFilter || {},
          [filterId]: selectedFilter
        }
      }

      if (state?.tempSelectedFilter?.[filterId] && facetId !== FACET_IDS.PRICE) {
        delete newState?.tempSelectedFilter?.[filterId];
      }


      break;
    }

    case 'APPLY_SELECTED_FILTER': {
      const {selectedFilter} = action.payload;
      const facetId = selectedFilter?.facet?.id;
      let filterId = selectedFilter?.filter?.id;

      if (facetId === FACET_IDS.PRICE) {
        filterId = FACET_IDS.PRICE
      }

      newState = {
        ...state,
        selectedFilter: {
          ...state?.selectedFilter || {},
          [filterId]: selectedFilter,
          ...state?.tempSelectedFilter || {},
        },
        tempSelectedFilter: {}
      }

      break;
    }

    case 'CLEAR_SELECTED_FILTER': {
      newState = {
        ...state,
        tempSelectedFilter: {}
      }

      break;
    }

    case 'CLEAR_APPLY_SELECTED_FILTER': {
      newState = {
        ...state,
        selectedFilter: {},
        tempSelectedFilter: {}
      }

      break;
    }

    case 'UPDATE_URL_FOR_REDIRECTION': {
      newState = {
        ...state,
        urlForRedirection: action.payload.value
      }

      break;
    }

    default:
      newState = state;
  }

  return newState;
}

export default filterReducer;
export {generateDefaultInitialFilterState};
